"use client";

/**
 * CalendarMultiSelector Component
 * 
 * Multi-select calendar picker with checkboxes/toggles for selecting multiple calendars
 * to view simultaneously.
 */

import * as React from "react";
import { Label } from "../label";
import { Button } from "../button";
import { Input } from "../input";
import { Users, User, ChevronDown, Search, Check } from "lucide-react";
import { useUniversalAuth } from "../../lib/auth/OptimalAuthProvider";
import { useMediaQuery } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";
import { ResponsiveModal } from "../responsive-modal";
import { useCalendarContext } from "../../lib/calendar-context";
import { getUnifiedCalendarSharedTeamPickerOptions } from "../../lib/unified-calendar-shared-team-calendars";

interface CalendarMultiSelectorProps {
  disabled?: boolean;
  className?: string;
  hideLabel?: boolean;
}

const AVAILABLE_PEOPLE = getUnifiedCalendarSharedTeamPickerOptions();

export const CalendarMultiSelector = ({ 
  disabled = false,
  className,
  hideLabel = false
}: CalendarMultiSelectorProps) => {
  const { appUser } = useUniversalAuth();
  const { selectedCalendars, toggleCalendar, calendarSources } = useCalendarContext();
  const [open, setOpen] = React.useState(false);

  const userEmail = appUser?.email;
  
  // Get all available calendar emails (from calendar sources + hardcoded people)
  const allCalendarOptions = React.useMemo(() => {
    const options: Array<{ value: string; label: string; isUserCalendar: boolean }> = [];
    
    // Add user's calendar first (always available)
    if (userEmail) {
      options.push({
        value: userEmail,
        label: "My Calendar",
        isUserCalendar: true,
      });
    }
    
    // Add calendars from calendar sources (connected calendars)
    calendarSources.forEach(source => {
      if (source.email !== userEmail && !options.find(o => o.value === source.email)) {
        options.push({
          value: source.email,
          label: source.email,
          isUserCalendar: false,
        });
      }
    });
    
    // Add hardcoded people
    AVAILABLE_PEOPLE.forEach(person => {
      if (!options.find(o => o.value === person.value)) {
        options.push({
          value: person.value,
          label: person.label,
          isUserCalendar: false,
        });
      }
    });
    
    return options;
  }, [userEmail, calendarSources]);

  // Simple search state
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return allCalendarOptions;
    return allCalendarOptions.filter(option => 
      option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.value.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allCalendarOptions, searchQuery]);

  const handleToggle = (email: string) => {
    toggleCalendar(email);
  };

  const getDisplayText = () => {
    if (selectedCalendars.length === 0) {
      return "Select calendars";
    }
    if (selectedCalendars.length === 1) {
      const option = allCalendarOptions.find(o => o.value === selectedCalendars[0]);
      return option?.label || selectedCalendars[0];
    }
    return `${selectedCalendars.length} calendars selected`;
  };

  return (
    <div className={className}>
      {!hideLabel && <Label>Calendars</Label>}
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          "w-full justify-between bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-10",
          !hideLabel && "mt-1",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <span className="truncate">{getDisplayText()}</span>
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
      </Button>

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title="Select Calendars"
        description="Choose which calendars to display"
        maxWidth="md"
        side="bottom"
        showCloseButton={true}
        className="flex flex-col"
      >
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search calendars..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="space-y-2 flex-1 overflow-y-auto">
            {filteredOptions.map((option, index) => {
              const isSelected = selectedCalendars.includes(option.value);
              const isUserCalendar = option.isUserCalendar;
              
              return (
                <Button
                  key={option.value}
                  variant="ghost"
                  onClick={() => handleToggle(option.value)}
                  className={cn(
                    "w-full justify-start p-3 h-auto space-x-3 hover:bg-card-50",
                    isSelected && "bg-blue-50 border border-blue-200"
                  )}
                >
                  <div className={cn(
                    "flex items-center justify-center w-5 h-5 border-2 rounded flex-shrink-0 transition-colors",
                    isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-card"
                  )}>
                    {isSelected && (
                      <Check className="h-4 w-4 text-white" />
                    )}
                  </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-mono mr-1">#{index}</span>
                        {isUserCalendar ? (
                          <Users className="h-4 w-4 text-gray-600 flex-shrink-0" />
                        ) : (
                          <User className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        )}
                        {option.label}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{option.value}</div>
                    </div>
                </Button>
              );
            })}
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

